import { useGetMedicationStatements } from '@hooks/api/medication';
import { useSession } from '@hooks/useSession';

import type { MedicationStatementRecord } from '@models/medication';

import { formatName } from '@utils/format';
import {
  getMedicationReportStatusLabel,
  groupMedicationsForReport,
} from '@utils/medication-report';

export type PdfMedicationEntry = {
  id: string;
  name: string;
  /** Human-readable status label (e.g. "On Hold", "Completed") — Past entries can hold several statuses. */
  status: string;
  acquisitionTypeName: string | null;
  dosage: string | null;
  route: string | null;
  startDate: string | null;
  endDate: string | null;
  instructions: string | null;
  notes: string | null;
};

export type PdfMedicationData = {
  patientName: string | null;
  initials: string;
  active: PdfMedicationEntry[];
  past: PdfMedicationEntry[];
};

function getInitials(firstName?: string | null, lastName?: string | null) {
  const first = firstName?.trim().charAt(0).toUpperCase() ?? '';
  const last = lastName?.trim().charAt(0).toUpperCase() ?? '';
  return first + last || '?';
}

export function useMedicationPdfData(): {
  pdfData: PdfMedicationData | null;
  loading: boolean;
  /** Query finished without usable medication data (Apollo error). */
  hasLoadError: boolean;
} {
  const {
    state: { activeProfile },
  } = useSession();
  // `no-cache`, not the default cache-first: the list page just ran this
  // exact query, so a cache hit would make `loading` false on the very first
  // render here, collapsing the gap before the auto-print effect fires (see
  // useAutoPrint) to nothing — the print dialog would appear to preempt the
  // route change instead of following it. Mirrors Health Summary's export.
  const { data, loading, error } = useGetMedicationStatements({
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return { pdfData: null, loading: true, hasLoadError: false };
  }

  if (error) {
    return { pdfData: null, loading: false, hasLoadError: true };
  }

  const toEntry = (m: MedicationStatementRecord): PdfMedicationEntry => ({
    id: m.id ?? '',
    name: m.medication ?? '',
    status: getMedicationReportStatusLabel(m),
    acquisitionTypeName: m.acquisitionTypeName ?? null,
    // The same line the list and the share view print, minus the route, which
    // the table gives its own column. `dose` used to build this cell and only
    // ever held the quantity — no strength, and an unguarded fall-through to
    // the legacy `medications/dose` extension that prints "dosage" on records
    // predating the migration.
    dosage: m.getDosageSummary({ route: false }) || null,
    route: m.route ?? null,
    startDate: m.getDateStartStr(),
    endDate: m.getDateEndStr(),
    instructions: m.instructions || null,
    notes:
      m.recordNotes
        .map((note) => note.text)
        .filter(Boolean)
        .join('; ') || null,
  });

  const { active, past } = groupMedicationsForReport(data);

  const pdfData: PdfMedicationData = {
    patientName:
      formatName(activeProfile?.first_name, activeProfile?.last_name) || null,
    initials: getInitials(activeProfile?.first_name, activeProfile?.last_name),
    active: active.map(toEntry),
    past: past.map(toEntry),
  };

  return { pdfData, loading: false, hasLoadError: false };
}
